/**
 * The crop editor's canvas: one acquisition at full resolution, unrotated, with
 * the frame that `crop` would take off shaded over it. The shading is a set of
 * OSD overlays anchored in image pixels, so it stays on the right pixels at any
 * zoom — which is the point: the edges are set by zooming in on them.
 *
 * Also reports where the pointer is, in millimetres from the image's top-left
 * corner, to read an edge's position straight off the image rather than by
 * trial and error.
 */
import { useEffect, useRef, useState } from 'react';
import OpenSeadragon, { Viewer } from 'openseadragon';
import {
    type Crop,
    croppedSize,
    cropToPixels,
    imageDpi,
    pxToMm,
} from '@/lib/crop';

type Size = { x: number; y: number };

// The four shaded bands, then the outline of what is kept.
const BANDS = ['top', 'bottom', 'left', 'right'] as const;

function bandRects(crop: Crop, size: Size) {
    const kept = croppedSize(size.x, size.y, crop);
    return {
        top: [0, 0, size.x, crop.top],
        bottom: [0, size.y - crop.bottom, size.x, crop.bottom],
        left: [0, crop.top, crop.left, kept.height],
        right: [size.x - crop.right, crop.top, crop.right, kept.height],
        frame: [crop.left, crop.top, kept.width, kept.height],
    } as const;
}

export default function ScannerCropViewer({
    imageUrl,
    crop,
    dpi,
    onSize,
}: {
    // IIIF info.json of the acquisition.
    imageUrl: string;
    // In millimetres.
    crop: Crop;
    // The acquisition's recorded DPI, if any.
    dpi: number | null;
    // The jp2's real pixel size, once Cantaloupe has said what it is.
    onSize?: (size: Size) => void;
}) {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const viewerRef = useRef<Viewer | null>(null);
    const itemRef = useRef<OpenSeadragon.TiledImage | null>(null);
    const overlays = useRef<Record<string, HTMLDivElement>>({});
    const [size, setSize] = useState<Size | null>(null);
    const [pointer, setPointer] = useState<Size | null>(null);
    const onSizeRef = useRef(onSize);
    onSizeRef.current = onSize;

    useEffect(() => {
        if (!elementRef.current) return;
        const viewer = new OpenSeadragon.Viewer({
            element: elementRef.current,
            drawer: 'canvas',
            showNavigationControl: false,
            imageLoaderLimit: 6,
            timeout: 60_000,
            // Edges are judged pixel by pixel: let the zoom go well past 1:1.
            maxZoomPixelRatio: 8,
        });
        viewerRef.current = viewer;
        viewer.addTiledImage({
            tileSource: imageUrl,
            success: (event: Event) => {
                const item = (
                    event as unknown as { item: OpenSeadragon.TiledImage }
                ).item;
                itemRef.current = item;
                const content = item.getContentSize();
                const measured = { x: content.x, y: content.y };
                setSize(measured);
                onSizeRef.current?.(measured);
            },
        });

        const tracker = new OpenSeadragon.MouseTracker({
            element: viewer.canvas,
            moveHandler: (e) => {
                const item = itemRef.current;
                if (!item) return;
                // The typings leave `position` off the generic event; OSD
                // sets it on every move.
                const { position } = e as unknown as {
                    position: OpenSeadragon.Point;
                };
                const p = item.viewerElementToImageCoordinates(position);
                setPointer({ x: Math.round(p.x), y: Math.round(p.y) });
            },
            leaveHandler: () => setPointer(null),
        });

        return () => {
            tracker.destroy();
            viewer.destroy();
            viewerRef.current = null;
            itemRef.current = null;
            overlays.current = {};
        };
    }, [imageUrl]);

    // ---- Shade the frame ----------------------------------------------------
    useEffect(() => {
        const viewer = viewerRef.current;
        const item = itemRef.current;
        if (!viewer || !item || !size) return;
        const rects = bandRects(
            cropToPixels(crop, imageDpi(dpi, size.x)),
            size,
        );
        for (const key of [...BANDS, 'frame'] as const) {
            const [x, y, w, h] = rects[key];
            const location = item.imageToViewportRectangle(x, y, w, h);
            let element = overlays.current[key];
            if (!element) {
                element = document.createElement('div');
                element.style.pointerEvents = 'none';
                element.style.cssText +=
                    key === 'frame'
                        ? 'outline:1px solid #ff4d4f;'
                        : 'background:rgba(255,77,79,0.35);';
                overlays.current[key] = element;
                viewer.addOverlay({ element, location });
            } else {
                viewer.updateOverlay(element, location);
            }
        }
    }, [crop, dpi, size]);

    const mm = (px: number) => size && pxToMm(px, imageDpi(dpi, size.x)).toFixed(1);

    return (
        <div className="relative h-full w-full bg-[#0a0e12] rounded-md overflow-hidden ring-1 ring-[#1e242a]">
            <div ref={elementRef} className="absolute inset-0" />
            <div className="pointer-events-none absolute left-3 bottom-3 rounded bg-black/60 px-2 py-1 font-mono text-xs text-[#d8dde2]">
                {size
                    ? `${mm(size.x)} × ${mm(size.y)} mm · ${Math.round(imageDpi(dpi, size.x))} dpi`
                    : 'Loading…'}
                {pointer && ` · x ${mm(pointer.x)}, y ${mm(pointer.y)} mm`}
            </div>
        </div>
    );
}
